// oxlint-disable no-console
// This optional code is used to register a service worker.
// register() is not called by default.
import { env } from 'config';
import Logger from 'utils/logger/Logger';

// Type declaration for process in browser environment
declare const process: { env: { [key: string]: string | undefined } };

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://github.com/facebook/create-react-app/blob/main/packages/cra-template/template/README.md

const LOG_PREFIX = 'ServiceWorkerRegister';
const verbose = true;

// Show a user-friendly notification that offline functionality is ready

const log = (message: string, ...params: unknown[]): void => {
  if (verbose) {
    Logger.warn(`${LOG_PREFIX}: ${message}`, ...params);
  }
};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === '[::1]' ||
  // 127.0.0.1/8 is considered localhost for IPv4.
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) ||
  window.location.hostname === 'workspace.vatix.local'
);

interface Config {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

// Track if registration is already in progress to prevent double registration
let isRegistering = false;
// Track if event listeners are already set up to prevent duplicates
let eventListenersSetup = false;

// Use API URL for health check so offline detection matches actual API availability
const healthCheckUrl = `${env.REACT_APP_API_URL}health/`;

export function register(config?: Config) {
  log('☘ registering service worker');
  // Skip SW registration in development: the dev server's module layout does
  // not match the production /assets/ cache-key pattern used in
  // checkCacheAndSetOfflineReady, so registering would either no-op silently
  // or corrupt offline-cache assumptions. Vite's define shim exposes
  // process.env.NODE_ENV the same way CRA did.
  if (process.env.NODE_ENV === 'development') {
    log('SW: skipping registration in development');
    return;
  }
  if ('serviceWorker' in navigator) {
    // Always set up event listeners, even if service worker is already active
    if (!eventListenersSetup) {
      setupNetworkEventListeners();
    }

    // Check if service worker is already registered or being registered
    if (navigator.serviceWorker.controller) {
      log('Service worker already active, skipping registration but ensuring event listeners are set up');
      // Ensure event listeners are set up even if service worker is already active
      if (!eventListenersSetup) {
        setupNetworkEventListeners();
      }
      // Check cache status even if service worker is already active
      checkCacheAndSetOfflineReady();
      return;
    }

    // Check if registration is already in progress
    if (isRegistering) {
      log('Service worker registration already in progress, skipping');
      return;
    }

    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    // Function to register the service worker
    const registerServiceWorker = () => {
      isRegistering = true;
      const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

      if (!isLocalhost) {
        // Is not local host. Just register service worker
        registerValidSW(swUrl);
      } else {
        // This is running on localhost. Lets check if a service worker still exists or not.
        checkValidServiceWorker(swUrl);
      }
    };

    // Check if the document is already loaded
    if (document.readyState === 'loading') {
      // Document is still loading, wait for the load event
      window.addEventListener('load', registerServiceWorker);
    } else {
      // Document has already loaded, register immediately
      // This may happen as we're calling register from a react component
      registerServiceWorker();
    }
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      isRegistering = false;

      // Ensure event listeners are set up when service worker is registered
      if (!eventListenersSetup) {
        setupNetworkEventListeners();
      }

      // Force activation of the service worker
      if (registration.waiting) {
        log('Service worker waiting, activating...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // If service worker is installed but not controlling, try to activate it
      if (registration.installing) {
        log('Service worker installing, waiting for activation...');
        registration.installing.addEventListener('statechange', () => {
          if (registration.installing?.state === 'installed') {
            log('Service worker installed, attempting activation...');
            // Force activation by reloading if needed
            if (!navigator.serviceWorker.controller) {
              log('No controller, reloading to activate service worker...');
              window.location.reload();
            }
          }
        });
      }

      // Only enable update detection in production
      // This prevents the service worker from trying to update during development
      // which causes the app to reload constantly
      if (process.env.NODE_ENV === 'production') {
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker == null) {
            return;
          }
          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // At this point, the updated precached content has been fetched,
                // but the previous service worker will still serve the older
                // content until all client tabs are closed.
                log('New content is available and will be used when all tabs for this page are closed');

                // Execute callback
                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                }
              } else {
                // At this point, everything has been precached.
                // It's the perfect time to display a
                // "Content is cached for offline use." message.
                log('Content is cached for offline use.');

                // Execute callback
                if (config && config.onSuccess) {
                  config.onSuccess(registration);
                }
              }
            }
          };
        };
      }
    })
    .catch((error) => {
      Logger.warn('Error during service worker registration:', error);
      isRegistering = false;
    });
}

function checkValidServiceWorker(swUrl: string) {
  log('🚀🚧 Checking valid service worker', swUrl);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl)
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      if (response.status === 404 || response.headers.get('content-type')?.indexOf('javascript') === -1) {
        // No service worker found. Probably a different app. Reload the page.
        log('🚀🚧 No service worker found', swUrl);
        navigator.serviceWorker.ready.then((registration) => {
          log('🚀🚧 Unregistering service worker', swUrl);
          registration.unregister().then(() => {
            log('🚀🚧 Reloading page', swUrl);
            window.location.reload();
          });
        });
      } else {
        log('🚀🚧 Registering service worker', swUrl);

        registerValidSW(swUrl);
      }
    })
    .catch(() => {
      log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        // Send cleanup message to service worker before unregistering
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEANUP',
          });
        }
        registration.unregister();
      })
      .catch((error) => {
        Logger.warn(error.message);
      });
  }
}

/**
 * Set up offline/online event listeners to communicate with service worker
 * Service workers don't have access to these events directly, so we need to
 * listen for them in the main thread and send messages to the service worker
 */
function setupNetworkEventListeners() {
  if (eventListenersSetup) {
    log('📡 Event listeners already set up, skipping');
    return;
  }

  eventListenersSetup = true;

  // Request notification permission for service worker notifications
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then((permission) => {
      log('📡 Notification permission:', permission);
    });
  }

  // Listen for offline/online events as a best-effort attempt
  // Note: These messages may not reach the service worker when going offline,
  // but the service worker independently checks network status every 30 seconds
  // during its periodic check, so it will detect offline status regardless.
  window.addEventListener('offline', () => {
    log('🦖 navigator.onLine:', navigator.onLine);
    // Send message to service worker (best effort - may fail when going offline)
    if (navigator.serviceWorker.controller) {
      try {
        log('🦖 Sending NETWORK_OFFLINE message to service worker');
        navigator.serviceWorker.controller.postMessage({
          type: 'NETWORK_OFFLINE',
        });
      } catch (error) {
        // Message may fail silently when going offline - this is expected
        // The service worker's periodic check will detect offline status independently
        log('🦖 Failed to send NETWORK_OFFLINE message (may be offline):', error);
      }
    } else {
      Logger.warn('🦖 No service worker controller available');
    }
  });

  // Listen for online events
  window.addEventListener('online', () => {
    log('📡 navigator.onLine:', navigator.onLine);
    // Send message to service worker (best effort)
    if (navigator.serviceWorker.controller) {
      try {
        log('📡 Sending NETWORK_ONLINE message to service worker');
        navigator.serviceWorker.controller.postMessage({
          type: 'NETWORK_ONLINE',
        });
      } catch (error) {
        // Message may fail - the service worker's periodic check will detect online status independently
        log('❌ Failed to send NETWORK_ONLINE message:', error);
      }
    } else {
      Logger.warn('❌ No service worker controller available');
    }
  });

  log('✅ Network event listeners set up for service worker communication');
  log('📡 Initial navigator.onLine:', navigator.onLine);

  // Send initial status to service worker if it's available
  if (navigator.serviceWorker.controller) {
    log('📡 Sending initial network status to service worker');
    navigator.serviceWorker.controller.postMessage({
      type: navigator.onLine ? 'NETWORK_ONLINE' : 'NETWORK_OFFLINE',
    });
    sendHealthCheckUrlMessage();
  } else {
    Logger.warn('⚠️ No service worker controller available during setup');
    // Try to wait for service worker to become ready
    navigator.serviceWorker.ready.then(() => {
      log('✅ Service worker ready, checking controller...');
      if (navigator.serviceWorker.controller) {
        log('✅ Service worker controller now available, sending initial status');
        navigator.serviceWorker.controller.postMessage({
          type: navigator.onLine ? 'NETWORK_ONLINE' : 'NETWORK_OFFLINE',
        });
        sendHealthCheckUrlMessage();

        // Check cache status when service worker becomes ready
        checkCacheAndSetOfflineReady();
      } else {
        Logger.warn('❌ Service worker ready but still no controller - may need page reload');
      }
    });
  }
}

// Check cache status and set offline ready state
async function checkCacheAndSetOfflineReady() {
  if ('caches' in window) {
    try {
      const cache = await caches.open('protector-v2');
      const keys = await cache.keys();

      const hasHtml = keys.some((key) => {
        const url = key.url;
        return url.endsWith('/') || url.includes('/publicForm/');
      });
      const hasJs = keys.some((key) => key.url.includes('/assets/') && key.url.endsWith('.js'));
      // This is never true on local development, so always return true on localhost
      const hasCss = isLocalhost || keys.some((key) => key.url.includes('/assets/') && key.url.endsWith('.css'));

      if (hasHtml && hasJs && hasCss) {
        // Dispatch custom event to notify components
        window.dispatchEvent(new CustomEvent('offlineReady', { detail: { ready: true } }));
        return true; // All resources ready
      }
    } catch (error) {
      Logger.warn('Error checking cache status:', error);
    }
  }
  return false; // Not ready yet
}

const sendHealthCheckUrlMessage = () => {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SET_HEALTH_CHECK_URL',
      payload: { url: healthCheckUrl },
    });
  }
};

// This is a temporary flag, remove once we want to enable offline submission for every organisation
export async function ensureServiceWorkerState(
  featureEnabled: boolean | null | undefined,
  { reloadAfterUnregister = true }: { reloadAfterUnregister?: boolean } = {}
): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  console.log('🚀🚧 ensureServiceWorkerState', featureEnabled);
  // Ignore while your org/feature flag is still loading (null/undefined)
  if (featureEnabled == null) {
    log('❌ offlineSubmission undefined, skipping SW toggle');
    return;
  }

  const reg = await navigator.serviceWorker.getRegistration();
  const isRegistered = !!reg;

  if (featureEnabled && !isRegistered) {
    log('🧩 Enabling SW…');
    register();
    // Check cache status periodically until ready
    const checkInterval = setInterval(async () => {
      const isReady = await checkCacheAndSetOfflineReady();
      if (isReady) {
        clearInterval(checkInterval);
      }
    }, 1000); // Check every second
    return;
  }

  // Also check cache status for existing service workers
  if (featureEnabled && isRegistered) {
    log('🧩 Service worker already registered, checking cache status...');
    // Ensure event listeners are set up even when SW is already registered
    if (!eventListenersSetup) {
      setupNetworkEventListeners();
    }
    checkCacheAndSetOfflineReady();
  }

  if (!featureEnabled && isRegistered) {
    log('🧹 Disabling SW…');
    // Send cleanup message to service worker before unregistering
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEANUP',
      });
    }
    await reg!.unregister();
    // Drop controller immediately to avoid "activated but deleted" in DevTools
    if (reloadAfterUnregister && navigator.serviceWorker.controller) {
      window.location.reload();
    }
    return;
  }

  sendHealthCheckUrlMessage();

  log('ℹ️ SW already in desired state', { featureEnabled, isRegistered });
}
